import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { reaction } from 'mobx';
import ClosedCaptionOutlinedIcon from '@mui/icons-material/ClosedCaptionOutlined';
import { CompositionStateStore } from '../../../librettoEditor/store/CompositionStateStore';
import { CompositionService } from '../../../librettoEditor/services/CompositionService';
import { ApplicationStore } from '../../../librettoEditor/store/ApplicationStore';
import { ContextMenuStore } from '../../../librettoEditor/store/ContextMenuStore';
import { TimelineStore } from '../../../librettoEditor/store/TimelineStore';
import { convertAbsTimeToUnits, convertTimeToUnits } from '../../../librettoEditor/utils/dom/convertTimeToUnits';
import { CLIP_CLASSNAME } from '../../../librettoEditor/modules/timeline/config/constants';
import { generateClipIdentifierClass } from '../../../librettoEditor/modules/timeline/utils/generateClipIdentifierClass';
import clsx from 'clsx';

interface CaptionsTimelineClipProps {
    clipId: string;
    layerId: string;
}

/**
 * Timeline clip component for CaptionsClip.
 * Displays as a thin bar with a captions icon and "Captions" label.
 * Custom implementation (not using generic Clip) for better control over context menu.
 */
const CaptionsTimelineClip: React.FC<CaptionsTimelineClipProps> = observer(({ clipId, layerId }) => {
    const [startTime, setStartTime] = useState(0);
    const [trimmedDuration, setTrimmedDuration] = useState(0);
    const [isFocused, setIsFocused] = useState(false);
    const [zoom, setZoom] = useState(TimelineStore.zoom);
    const elementRef = useRef<HTMLDivElement>(null);

    // Watch for clip state changes
    useEffect(() => {
        const disposer = reaction(
            () => ({
                clip: CompositionStateStore.clips?.[clipId],
                selectedClipId: ApplicationStore.selectedClipId,
                zoom: TimelineStore.zoom,
            }),
            ({ clip, selectedClipId, zoom: newZoom }) => {
                if (!clip) return;
                setStartTime(clip.startTime);
                setTrimmedDuration(clip.trimmedDuration);
                setIsFocused(selectedClipId === clip.id);
                setZoom(newZoom);
            },
            { fireImmediately: true }
        );
        return () => disposer();
    }, [clipId]);

    // Calculate position and width based on timeline zoom
    const x = convertAbsTimeToUnits(startTime);
    const width = convertTimeToUnits(trimmedDuration);

    const handleMouseDown = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        ApplicationStore.setSelectedClipId(clipId);
    };

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Select this clip on right-click if not already selected
        if (ApplicationStore.selectedClipId !== clipId) {
            ApplicationStore.setSelectedClipId(clipId);
        }

        const menuItems = [];

        // Layer order submenu (captions are visual elements)
        menuItems.push({
            title: "Layer order",
            children: [
                { 
                    title: "Bring to front", 
                    onClick: () => { 
                        CompositionService.bringVisualToFront(clipId); 
                        ContextMenuStore.hide(); 
                    } 
                },
                { 
                    title: "Bring forward", 
                    onClick: () => { 
                        CompositionService.bringVisualForward(clipId); 
                        ContextMenuStore.hide(); 
                    } 
                },
                { 
                    title: "Send backward", 
                    onClick: () => { 
                        CompositionService.sendVisualBackward(clipId); 
                        ContextMenuStore.hide(); 
                    } 
                },
                { 
                    title: "Send to back", 
                    onClick: () => { 
                        CompositionService.sendVisualToBack(clipId); 
                        ContextMenuStore.hide(); 
                    } 
                },
            ],
        });

        // Delete
        menuItems.push({
            title: "Delete",
            onClick: () => {
                CompositionService.deleteClipById(clipId);
                ContextMenuStore.hide();
            },
        });

        ContextMenuStore.show(e.clientX, e.clientY, menuItems);
    };

    return (
        <div
            ref={elementRef}
            id={clipId}
            className={clsx(CLIP_CLASSNAME, generateClipIdentifierClass(clipId), {
                clip__focused: isFocused,
            })}
            style={{
                position: 'absolute',
                transform: `translateX(${x}px)`,
                width: `${width}px`,
                height: '100%',
                backgroundColor: '#cccccc',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                paddingLeft: '8px',
                paddingRight: '8px',
                cursor: 'pointer',
                overflow: 'hidden',
                border: isFocused ? '2px solid rgba(43, 98, 253, 1)' : '1px solid #6B7280',
                boxSizing: 'border-box',
            }}
            onMouseDown={handleMouseDown}
            onContextMenu={handleContextMenu}
        >
            <ClosedCaptionOutlinedIcon
                sx={{
                    fontSize: 14,
                    color: 'rgba(100, 100, 100, 0.9)',
                    flexShrink: 0,
                }}
            />
            <span
                style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#1a1a1a',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                Captions
            </span>
        </div>
    );
});

export default CaptionsTimelineClip;
